// src/components/AuditLogs.jsx
// HIPAA Audit Log Viewer for Admins
import React, { useState, useEffect } from "react";
import {
  ClipboardList,
  Search,
  Download,
  RefreshCw,
  User,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Upload,
  FileText,
  Shield,
  AlertTriangle
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || '';

// Action icons mapping
const ACTION_ICONS = {
  VIEW_CLIENT: <Eye size={14} className="text-blue-600" />,
  LIST_CLIENTS: <ClipboardList size={14} className="text-blue-600" />,
  CREATE_CLIENT: <User size={14} className="text-green-600" />,
  UPDATE_CLIENT: <Edit size={14} className="text-amber-600" />,
  DELETE_CLIENT: <Trash2 size={14} className="text-red-600" />,
  EXPORT_CLIENTS: <Download size={14} className="text-purple-600" />,
  BULK_IMPORT: <Upload size={14} className="text-indigo-600" />,
  SEARCH_CLIENTS: <Search size={14} className="text-blue-600" />,
  VIEW_PRESCRIPTIONS: <FileText size={14} className="text-teal-600" />,
  SYNC_BLUEBUTTON: <RefreshCw size={14} className="text-blue-600" />,
  VIEW_COMMUNICATIONS: <Eye size={14} className="text-gray-600" />,
  CREATE_COMMUNICATION: <Edit size={14} className="text-green-600" />,
  LOGIN_SUCCESS: <Shield size={14} className="text-green-600" />,
  LOGIN_FAILED: <AlertTriangle size={14} className="text-red-600" />,
};

// Format action for display
const formatAction = (action) => {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Format date for display
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [actions, setActions] = useState([]);

  // Load available actions for filter dropdown
  useEffect(() => {
    loadActions();
    loadSummary();
  }, []);

  // Load logs when filters change
  useEffect(() => {
    loadLogs();
  }, [actionFilter, dateRange, pagination.offset]);

  const loadActions = async () => {
    try {
      const res = await fetch(`${API_BASE}/audit/actions`, {
        credentials: 'include'
      });
      if (res.ok) {
        const json = await res.json();
        setActions(json.actions || []);
      }
    } catch (err) {
      console.error('Failed to load actions:', err);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await fetch(`${API_BASE}/audit/summary?days=${dateRange}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const json = await res.json();
        setSummary(json);
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });

      if (actionFilter) {
        params.append('action', actionFilter);
      }

      // Calculate start date based on dateRange
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));
      params.append('start_date', startDate.toISOString());

      const res = await fetch(`${API_BASE}/audit/logs?${params}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const json = await res.json();
        setLogs(json.data || []);
        setPagination(prev => ({ ...prev, total: json.pagination?.total || 0 }));
      } else if (res.status === 403) {
        setError('Admin access required to view audit logs');
      } else {
        setError('Failed to load audit logs');
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async (format) => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const params = new URLSearchParams({
        format,
        start_date: startDate.toISOString()
      });

      const res = await fetch(`${API_BASE}/audit/export?${params}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (err) {
      console.error('Failed to export logs:', err);
      alert('Failed to export audit logs');
    }
  };

  const nextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const prevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow p-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-4">
          <ClipboardList className="text-red-600" size={24} />
          <div className="text-xl font-bold text-[#172A3A]">Audit Logs</div>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total PHI Access</div>
            <div className="text-2xl font-bold text-[#172A3A]">{summary.summary?.totalPhiAccess || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Clients Accessed</div>
            <div className="text-2xl font-bold text-[#172A3A]">{summary.summary?.uniqueClientsAccessed || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Failed Attempts</div>
            <div className="text-2xl font-bold text-red-600">{summary.summary?.failedAccessAttempts || 0}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Data Exports</div>
            <div className="text-2xl font-bold text-purple-600">{summary.summary?.exportOperations || 0}</div>
          </div>
        </div>
      )}

      {/* Main Audit Log Panel */}
      <div className="bg-white rounded-2xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ClipboardList className="text-blue-600" size={24} />
            <div className="text-xl font-bold text-[#172A3A]">Audit Logs</div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => exportLogs('csv')}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
            >
              <Download size={16} /> Export CSV
            </button>
            <button
              onClick={loadLogs}
              className="flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <div>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#FFB800]"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>

          <div>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#FFB800]"
            >
              <option value="">All Actions</option>
              {actions.map(action => (
                <option key={action} value={action}>{formatAction(action)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-gray-400" size={24} />
            <span className="ml-2 text-gray-500">Loading audit logs...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No audit logs found for the selected filters.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-600">
                    <th className="pb-3 pr-4">Timestamp</th>
                    <th className="pb-3 pr-4">Action</th>
                    <th className="pb-3 pr-4">User</th>
                    <th className="pb-3 pr-4">Details</th>
                    <th className="pb-3">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-gray-700">{formatDate(log.createdAt)}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          {ACTION_ICONS[log.action] || <ClipboardList size={14} className="text-gray-400" />}
                          <span className="text-sm font-medium text-[#172A3A]">
                            {formatAction(log.action)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-sm text-gray-700">
                          {log.userId?.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {log.clientId && (
                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs mr-2">
                              Client: {log.clientId.slice(0, 8)}...
                            </span>
                          )}
                          {log.details?.recordCount && (
                            <span className="text-gray-500">{log.details.recordCount} records</span>
                          )}
                          {log.details?.statusCode && (
                            <span className={`ml-2 ${log.details.statusCode < 400 ? 'text-green-600' : 'text-red-600'}`}>
                              ({log.details.statusCode})
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <span className="text-sm text-gray-500 font-mono">
                          {log.ipAddress}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={prevPage}
                  disabled={pagination.offset === 0}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* HIPAA Compliance Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="text-blue-600 mt-0.5" size={20} />
          <div>
            <div className="font-semibold text-blue-900">HIPAA Compliance</div>
            <div className="text-sm text-blue-700 mt-1">
              Audit logs are retained for 7 years as required by HIPAA. Logs are immutable and cannot be modified or deleted.
              All access to Protected Health Information (PHI) is recorded.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
